/**
 * TranslationDrawer — bir kaydın EN (ve ileride başka dil) çevirilerini düzenler.
 *
 * Çeviri yazma JSON-only'dir: PATCH /{resource}/{id}/ body
 * `{ translations: { en: { ...fields } } }`. Görsel upload (multipart) yolundan
 * ayrı tutulur — bu yüzden ResourceForm'a gömmek yerine ayrı drawer. Boş bırakılan
 * alan backend'de TR temel kayda düşer (alan-bazlı fallback).
 *
 * Kullanım (Products/Categories):
 *   <TranslationDrawer
 *     opened={!!trItem} onClose={() => setTrItem(null)}
 *     resource="products" id={trItem?.id}
 *     baseValues={{ name: trItem?.name, ingredients_text: trItem?.ingredients_text }}
 *     translations={trItem?.translations}
 *     fields={[{ name: "name", label: "Ad" }, ...]}
 *     invalidateKeys={[["products"]]}
 *   />
 */
import { Drawer, Stack, TextInput, Textarea, Button, Group, Text, Title } from "@mantine/core";
import { IconDeviceFloppy } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../api";
import { patchItemInCache } from "../api/resources";

export interface TranslationField {
  name: string;
  label: string;
  textarea?: boolean;
  /** TR temel değer — placeholder olarak "boşsa şu kullanılır" ipucu verir. */
  placeholder?: string;
}

export function TranslationDrawer({
  opened,
  onClose,
  resource,
  id,
  title = "Çeviriler (EN)",
  fields,
  baseValues = {},
  translations,
  invalidateKeys = [],
  lang = "en",
}: {
  opened: boolean;
  onClose: () => void;
  resource: string;
  id: string | null | undefined;
  title?: string;
  fields: TranslationField[];
  baseValues?: Record<string, string | undefined>;
  translations?: Record<string, Record<string, string>> | undefined;
  invalidateKeys?: unknown[][];
  lang?: string;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<Record<string, string>>({});

  // Drawer her açılışında mevcut çeviriyi yükle (TR temel kayıt DEĞİL — sadece
  // çeviri satırı; boş alan = TR'ye düşecek).
  useEffect(() => {
    if (opened) {
      const current = translations?.[lang] || {};
      const seed: Record<string, string> = {};
      for (const f of fields) seed[f.name] = current[f.name] || "";
      setForm(seed);
    }
  }, [opened, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = { translations: { [lang]: form } };
      return (await apiClient.patch(`/api/v1/${resource}/${id}/`, payload)).data;
    },
    onSuccess: (updated) => {
      // Sonsuz liste yerinde düzeltilir (tüm sayfalar yeniden çekilmez).
      invalidateKeys.forEach((k) => {
        qc.setQueriesData({ queryKey: k }, (old: any) => patchItemInCache(old, updated));
        qc.invalidateQueries({ queryKey: k, predicate: (q) => q.queryKey[1] !== "infinite" });
      });
      notifications.show({ message: "Çeviri kaydedildi", color: "green" });
      onClose();
    },
    onError: (e: any) =>
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" }),
  });

  return (
    <Drawer opened={opened} onClose={onClose} title={<Title order={4}>{title}</Title>} position="right" size="md">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Boş bırakılan alanlar müşteriye Türkçe (temel) değeriyle gösterilir.
        </Text>
        {fields.map((f) => {
          // key spread'le geçirilmez (React uyarısı) → doğrudan JSX'e ver.
          const common = {
            label: f.label,
            placeholder: baseValues[f.name] || f.placeholder || "",
            value: form[f.name] || "",
            onChange: (e: any) =>
              setForm({ ...form, [f.name]: e.currentTarget.value }),
          };
          return f.textarea ? (
            <Textarea key={f.name} {...common} autosize minRows={2} />
          ) : (
            <TextInput key={f.name} {...common} />
          );
        })}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>İptal</Button>
          <Button
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={!id}
          >
            Kaydet
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
